import React from 'react';
import ReactDOM, { Renderer } from 'react-dom';
import { fireLoadEventToSite } from '@ff-os/pdp-shared-redux/global-state/site-events';
import { IDT, usesDesignerContainer, usesModalContainer } from '@ff-os/pdp-shared-redux/IDT';
import { defaultDisplayMode, defaultLanguage } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { createStore, PdpStore } from '@ff-os/pdp-shared-redux/store';
import { createReactI18n } from '@ff-os/shared-translations/localization';
import { preloadResources } from '../helpers/preloadResources';
import { registerSiteInteractions } from '../helpers/registerSiteInteractions';
import { setStartupProjectFromScene } from '../helpers/setStartupProjectFromScene';
import { startup } from '../helpers/startup';
import { tryLoadProject } from '../helpers/tryLoadProject';
import { DesignerUi } from '../ui/DesignerUi';
import { ModalUi } from '../ui/ModalUi';
import { PreviewUi } from '../ui/PreviewUi';
import { reportConfigurationErrors } from './reportConfigurationErrors';

const hydrate = async (options: IDT.HydrateOptions) => {
    const language = window.__idt_language;
    const displayMode = window.__idt_display_mode;

    reportConfigurationErrors({ ...options, language, mode: displayMode });

    const store = createStore(window.__idt_state);

    registerSiteInteractions(store, options, displayMode);

    await preloadResources(store, language);

    startup(store, language, displayMode);

    renderUi(ReactDOM.hydrate, store, options);

    tryLoadProject(store, options.open);

    fireLoadEventToSite({ name: 'load' });
};

const render = async (options: IDT.Options) => {
    const language = options.language || defaultLanguage;
    const displayMode = options.mode || defaultDisplayMode;

    reportConfigurationErrors({ ...options, language, mode: displayMode });

    const store = createStore(setStartupProjectFromScene(window.__idt_state));

    registerSiteInteractions(store, options, displayMode);

    await preloadResources(store, language);

    startup(store, language, displayMode);

    renderUi(ReactDOM.render, store, options);

    tryLoadProject(store, options.open);

    fireLoadEventToSite({ name: 'load' });
};

const renderUi = (renderer: Renderer, store: PdpStore, options: IDT.Options | IDT.HydrateOptions) => {
    const i18n = createReactI18n(store.getState().global.language);

    if (usesDesignerContainer(options.containers)) {
        renderer(<DesignerUi store={store} i18n={i18n} />, options.containers.designer);
    }

    if (usesModalContainer(options.containers)) {
        renderer(<ModalUi store={store} i18n={i18n} />, options.containers.modal);
    }

    if (options.containers.preview) {
        renderer(<PreviewUi store={store} i18n={i18n} />, options.containers.preview);
    }
};

export const idtApi = {
    hydrate,
    render
};